import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CandidateBanner from '../components/CandidateBanner';
import Field from '../components/Field';
import { evaluateCondition, getPath } from '../lib/conditions';
import { usePageIdentity } from '../lib/usePageIdentity';
import { API_BASE } from '../firebase';
import RichText from '../components/RichText';

/**
 * The candidate conflict-disclosure form, filled in by someone outside the firm.
 *
 * OUTSIDE THE AUTH GATE, on purpose and by the same reasoning as /approve: the signed token in
 * the URL is the credential. A job candidate has no firm account and never will — the session
 * path deliberately refuses anyone outside @aalrr.com — so requiring a sign-in would make this
 * form impossible to fill in rather than more secure.
 *
 * Everything is decided server-side. This page renders whatever `/candidate/context` says the
 * questions are, and the server re-validates on submit whatever this page believed. It holds no
 * schema of its own: the questions are a firm's legal wording and change without a deploy.
 *
 * The controls are the intake form's own `Field` component, which is why a five-column repeatable
 * table, a Yes/No and a conditional reveal all work here without being written twice.
 */

const LINK_MESSAGES = {
  missing_token: 'This link is incomplete. Open the link from your invitation email exactly as it was sent.',
  malformed_token: 'This link is not valid. Open it from your invitation email.',
  bad_signature: 'This link is not valid. Open it from your invitation email.',
  malformed_payload: 'This link is not valid. Open it from your invitation email.',
  expired: 'This link has expired. Reply to your invitation email and we will send a new one.',
  wrong_link: 'This link does not match this form. Open the link from your invitation email.',
  // Two different dead links, and the advice differs. A revoked link has no newer email to find,
  // so telling somebody to look for one sends them after something that does not exist.
  link_superseded: 'This link has been replaced by a newer one. Please open the most recent email we sent you.',
  link_revoked:
    'This link has been switched off. If you were expecting to complete a form, reply to the email we sent you and we will look into it.',
  token_revoked: 'This link is no longer active. Reply to the email we sent you and we will help.',
  not_found: 'We could not find this form.',
};

/** Refusals that mean this link can no longer change the form, whatever is retried. */
const DEAD_LINK_REASONS = ['expired', 'link_superseded', 'link_revoked', 'token_revoked', 'not_found', 'wrong_link', 'bad_signature'];

/**
 * A network failure comes back as a refusal rather than a throw. It used to throw past
 * `setSaving(false)` / `setSubmitting(false)`, leaving the buttons stuck on "Saving…" and
 * "Sending…" until the page was reloaded.
 */
async function postJson(path, body) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    return { res: { ok: false, status: 0 }, body: { reason: 'network' } };
  }
  let parsed = {};
  try {
    parsed = await res.json();
  } catch {
    parsed = {};
  }
  return { res, body: parsed };
}

export default function CandidateForm() {
  const [params] = useSearchParams();
  const formId = params.get('form');
  const token = params.get('token');

  /**
   * The candidate's own tab. Worded for them, not for the firm: they have no idea what a "new
   * matter" is, and this is what sits in their tab strip — and in a bookmark, and in their
   * history — while they work through four pages of it.
   */
  usePageIdentity({
    title: 'Conflict Disclosure Form — AALRR',
    icon: '/ccc-favicon.svg',
  });

  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState(null);
  const [sections, setSections] = useState([]);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [bypasses, setBypasses] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const [saveProblem, setSaveProblem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [blocking, setBlocking] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!formId || !token) {
        setLinkError(LINK_MESSAGES.missing_token);
        setLoading(false);
        return;
      }
      const { res, body } = await postJson('/candidate/context', { form: formId, token });
      if (cancelled) return;
      if (!res.ok) {
        setLinkError(LINK_MESSAGES[body.reason] || 'This link could not be opened.');
        setLoading(false);
        return;
      }
      setSections(body.sections || []);
      setForm(body.form || null);
      setAnswers((body.form && body.form.answers) || {});
      setBypasses((body.form && body.form.bypasses) || {});
      setAttachments((body.form && body.form.attachments) || []);
      setSubmitted((body.form && body.form.status) === 'submitted');
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [formId, token]);

  /**
   * Progress is saved automatically a few seconds after typing stops.
   *
   * This form asks for five years of work history; somebody will fill it in over more than one
   * sitting, and a closed tab that loses the lot is the difference between a form that comes back
   * and one that does not. The delay is so a save is not fired on every keystroke.
   */
  const pending = useRef(null);
  const latest = useRef({ answers, bypasses });
  latest.current = { answers, bypasses };

  const save = useCallback(
    async ({ quiet } = {}) => {
      if (submitted) return;
      if (!quiet) setSaving(true);
      const { res, body } = await postJson('/candidate/save', {
        form: formId,
        token,
        answers: latest.current.answers,
        bypasses: latest.current.bypasses,
      });
      setSaving(false);
      if (res.ok) {
        setSavedAt(body.savedAt || new Date().toISOString());
        setSaveProblem(null);
        return;
      }
      /**
       * A refused save is said out loud. It used to be ignored, so the old "Saved 3:02 PM" stayed
       * on screen while everything typed after it was thrown away — most often because somebody
       * in the firm had sent a newer link while this one was open.
       */
      if (body.reason === 'already_submitted') {
        setSubmitted(true);
        return;
      }
      // Another tab is submitting this form; that submit is the record, and saving over it is
      // exactly what the server now refuses.
      if (body.reason === 'submit_in_progress') return;
      setSaveProblem(
        DEAD_LINK_REASONS.includes(body.reason)
          ? `Your latest changes have not been saved. ${LINK_MESSAGES[body.reason] || ''}`.trim()
          : body.reason === 'network'
            ? 'Your latest changes have not been saved — we could not reach the firm. Check your connection; we will try again when you next make a change, or press Save and finish later.'
            : 'Your latest changes have not been saved. Please press Save and finish later to try again.'
      );
    },
    [formId, token, submitted]
  );

  const touch = useCallback(() => {
    if (submitted) return;
    if (pending.current) clearTimeout(pending.current);
    pending.current = setTimeout(() => save({ quiet: true }), 2500);
  }, [save, submitted]);

  useEffect(() => () => pending.current && clearTimeout(pending.current), []);

  /**
   * `Field` emits the WHOLE next answers object, not the edited value.
   *
   * See components/Field.jsx: it wraps its inner control as
   * `onChange(setPath(matter, field.path, next))` — the path is applied inside the component, so
   * the callback receives a complete object. IntakeForm's `update(next)` has always taken it that
   * way. This page treated the argument as the field's value and set it AT the path a second
   * time, which stored the whole object inside itself and rendered "[object Object]" in the one
   * editable box in the header.
   *
   * A build could not see it and neither could a type-free callback signature; it showed up the
   * first time somebody typed in the field.
   */
  const change = (nextAnswers) => {
    setAnswers(nextAnswers);
    touch();
  };

  const visibleSections = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        fields: (section.fields || []).filter(
          (field) => field.visibleIf === undefined || evaluateCondition(field.visibleIf, answers)
        ),
      })),
    [sections, answers]
  );

  const problemFor = (path) => blocking.find((p) => p.path === path);

  /**
   * Uploading a matter list.
   *
   * The file goes up on its own request, immediately, rather than riding along with the next
   * draft save: a candidate who attaches a spreadsheet and closes the tab should find it there
   * when they come back, and an upload that waits for a debounce is an upload that can be lost.
   *
   * The bytes are base64 in JSON, which is what the endpoint expects — it keeps this on the same
   * path as every other call this page makes. FileReader gives a data: URL, so the prefix up to
   * the comma is dropped.
   */
  const upload = async (file) => {
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
        reader.onerror = () => reject(new Error('unreadable'));
        reader.readAsDataURL(file);
      });
      const { res, body } = await postJson('/candidate/upload', {
        form: formId,
        token,
        filename: file.name,
        data,
      });
      if (res.ok) setAttachments(body.attachments || []);
      else
        setUploadError(
          body.message ||
            (res.status === 413
              ? 'That file is too large to upload here.'
              : 'That file could not be uploaded. Please try again.')
        );
    } catch {
      setUploadError('That file could not be read.');
    } finally {
      setUploading(false);
    }
  };

  const removeUpload = async (attachmentId) => {
    setUploadError(null);
    const { res, body } = await postJson('/candidate/upload/remove', { form: formId, token, attachmentId });
    if (res.ok) setAttachments(body.attachments || []);
    else setUploadError('That file could not be removed. Please try again.');
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    if (pending.current) clearTimeout(pending.current);
    const { res, body } = await postJson('/candidate/submit', {
      form: formId,
      token,
      answers,
      bypasses,
    });
    setSubmitting(false);
    if (res.ok) {
      setBlocking([]);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (body.error === 'incomplete') {
      setBlocking(body.blocking || []);
      // Straight to the first thing that needs attention, rather than leaving somebody to hunt
      // for it in four pages.
      const first = (body.blocking || [])[0];
      if (first) {
        const node = document.getElementById(`field-${first.path}`);
        if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    if (body.reason === 'already_submitted') {
      setSubmitted(true);
      return;
    }
    // Only claims the answers are saved when the last save actually succeeded.
    setSubmitError(
      DEAD_LINK_REASONS.includes(body.reason)
        ? `Your form could not be sent. ${LINK_MESSAGES[body.reason] || ''}`.trim()
        : body.reason === 'submit_in_progress'
          ? 'Your form is already being sent — from this page or another tab. Give it a minute, then reload this page.'
          : body.reason === 'network'
            ? 'We could not reach the firm to send your form. Your answers are still on this page; check your connection and try again.'
            : `Something went wrong sending your form.${
                savedAt && !saveProblem ? ' Your answers are saved;' : ''
              } Please try again in a moment.`
    );
  };

  const pdfUrl = `${API_BASE}/candidate/pdf?form=${encodeURIComponent(formId || '')}&token=${encodeURIComponent(
    token || ''
  )}`;

  if (loading) {
    return <Frame><p className="text-slate-500 dark:text-slate-400">Opening your form…</p></Frame>;
  }
  if (linkError) {
    return (
      <Frame>
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          {linkError}
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      {/* The heading and the firm's name are in the banner now — repeating them here put the
          same two lines twice on the page, which is what happened on /ccc when it left the
          Shell. */}
      {submitted ? (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-100">
          <p className="font-medium">Thank you — your form has been submitted.</p>
          <p className="mt-1">
            It has been sent to the firm. Keep a copy for your records if you would like one.
          </p>
          <a
            href={pdfUrl}
            className="mt-3 inline-block rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:bg-firm-500 dark:hover:bg-firm-400"
          >
            Download a PDF copy
          </a>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
          <p>
            Please answer every question. The longest part asks about the legal work you have done
            over the past five years, so it is worth having your records to hand.
          </p>
          <p className="mt-2">
            <strong>Your answers save automatically.</strong> You can close this page and come back
            to the same link.
          </p>
        </div>
      )}

      {blocking.length > 0 && !submitted && (
        <div className="mt-4 rounded-lg border border-red-400 bg-red-50 p-4 text-sm text-red-900 dark:border-red-600 dark:bg-red-950 dark:text-red-100">
          <p className="font-medium">
            {blocking.length === 1 ? 'One answer still needs' : `${blocking.length} answers still need`} your
            attention:
          </p>
          <ul className="mt-2 list-disc pl-5">
            {blocking.map((problem) => (
              <li key={problem.path}>{problem.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={submitted ? 'pointer-events-none opacity-70' : ''}>
        {visibleSections.map((section) => (
          <section key={section.id} className="mt-6">
            <h2 className="text-base font-semibold text-ink dark:text-slate-100">
              <RichText>{section.title}</RichText>
            </h2>
            {section.description && (
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                <RichText>{section.description}</RichText>
              </p>
            )}
            <div className="mt-3 space-y-4">
              {section.fields.map((field) => (
                <div key={field.path} id={`field-${field.path}`}>
                  {/*
                    A locked field is a FACT from the invitation, not an input. Rendered as a
                    disabled control it read as an empty box with grey placeholder text — the
                    candidate's own name looked like a hint they were being asked to supply. As
                    label and value it reads as what it is, and the Office select stops looking
                    like an empty dropdown.
                  */}
                  {/*
                    The upload is rendered here rather than as a case in Field.jsx, because it is
                    the one control on this page that talks to the server itself: it needs the
                    form id and the token, which are this page's and nothing else's.
                  */}
                  {field.type === 'candidateFiles' ? (
                    <div className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                      <div className="text-sm font-medium text-ink dark:text-slate-100">{field.label}</div>
                      {field.helpText ? (
                        <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                          <RichText>{field.helpText}</RichText>
                        </p>
                      ) : null}

                      {attachments.length > 0 && (
                        <ul className="mt-3 space-y-1">
                          {attachments.map((file) => (
                            <li
                              key={file.id}
                              className="flex items-center justify-between gap-3 rounded border border-slate-200 px-2 py-1.5 text-sm dark:border-slate-700"
                            >
                              <span className="min-w-0 truncate text-ink dark:text-slate-100">
                                {file.filename}
                                <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                                  {Math.max(1, Math.round(file.size / 1024))} KB
                                </span>
                              </span>
                              {!submitted && (
                                <button
                                  type="button"
                                  onClick={() => removeUpload(file.id)}
                                  className="shrink-0 text-xs text-red-600 hover:underline dark:text-red-400"
                                >
                                  Remove
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/*
                        The template is what makes the upload checkable: its columns are this
                        form's Matters table, and the server accepts only a completed copy of it
                        (functions/src/candidate/matterTemplate.js), refusing anything else with a
                        message saying what to fix.
                      */}
                      {!submitted && (
                        <a
                          href={`${API_BASE}/candidate/template`}
                          download
                          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-firm-700 underline dark:text-firm-200"
                        >
                          Download the matter list template (.xlsx)
                        </a>
                      )}
                      {!submitted && (
                        <label className="mt-3 ml-0 flex w-fit cursor-pointer items-center gap-2 rounded border border-dashed border-slate-400 px-3 py-1.5 text-xs text-slate-700 hover:border-firm-500 dark:border-slate-500 dark:text-slate-200">
                          <input
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            className="sr-only"
                            disabled={uploading}
                            onChange={(event) => {
                              const file = event.target.files && event.target.files[0];
                              // Cleared so choosing the same file twice still fires a change.
                              event.target.value = '';
                              upload(file);
                            }}
                          />
                          {uploading ? 'Checking and uploading…' : '+ Upload your completed template'}
                        </label>
                      )}
                      {uploadError && (
                        <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
                          {uploadError}
                        </p>
                      )}
                    </div>
                  ) : field.locked === true ? (
                    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
                      <div className="text-xs text-slate-500 dark:text-slate-400">{field.label}</div>
                      <div className="text-[15px] text-ink dark:text-slate-100">
                        {readable(getPath(answers, field.path)) || (
                          <span className="text-slate-400 dark:text-slate-500">Not given</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <Field
                      field={field}
                      matter={answers}
                      config={{}}
                      onChange={change}
                      disabled={submitted}
                      problem={problemFor(field.path)}
                      inputId={field.type === 'checkbox' ? field.path : undefined}
                    />
                  )}
                  {/*
                    A soft-required question left blank asks for a reason rather than refusing the
                    form. A candidate called to the bar this year truthfully has no five-year work
                    history, and a hard requirement would make the honest answer the one that
                    cannot be submitted.
                  */}
                  {field.requirement === 'soft_required' &&
                    isBlank(getPath(answers, field.path)) &&
                    // An uploaded matter list is the answer, so stop asking why the table is empty.
                    !(field.satisfiedByAttachment === true && attachments.length > 0) &&
                    !submitted && (
                    <label className="mt-2 block text-xs text-slate-600 dark:text-slate-300">
                      If there is nothing to list here, tell us briefly why:
                      <textarea
                        className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
                        rows={2}
                        value={bypasses[field.path] || ''}
                        onChange={(e) => {
                          setBypasses((prev) => ({ ...prev, [field.path]: e.target.value }));
                          touch();
                        }}
                      />
                    </label>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {!submitted && (
        <div className="sticky bottom-0 mt-8 flex flex-wrap items-center gap-3 border-t border-slate-200 bg-page/95 py-4 backdrop-blur dark:border-slate-700">
          <button
            type="button"
            onClick={submit}
            // Not while a file is still going up: the submit would fix what HR receives without it.
            disabled={submitting || uploading}
            className="rounded bg-firm-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
          >
            {submitting ? 'Sending…' : 'Submit my form'}
          </button>
          <button
            type="button"
            onClick={() => save()}
            disabled={saving}
            className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:border-firm-500 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200"
          >
            {saving ? 'Saving…' : 'Save and finish later'}
          </button>
          <a href={pdfUrl} className="text-sm text-firm-700 underline dark:text-firm-300">
            Preview as PDF
          </a>
          {saveProblem ? (
            <span className="ml-auto text-xs font-medium text-red-700 dark:text-red-400">Not saved</span>
          ) : (
            <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
              {savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString()}` : 'Not saved yet'}
            </span>
          )}
          {saveProblem && (
            <p role="alert" className="w-full rounded border border-red-400 bg-red-50 p-3 text-sm text-red-900 dark:border-red-600 dark:bg-red-950 dark:text-red-100">
              {saveProblem}
            </p>
          )}
        </div>
      )}

      {submitError && (
        <p className="mt-3 rounded border border-red-400 bg-red-50 p-3 text-sm text-red-900 dark:border-red-600 dark:bg-red-950 dark:text-red-100">
          {submitError}
        </p>
      )}
    </Frame>
  );
}

/** A stored answer as text, for the read-only header rows. */
const readable = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map((entry) => String(entry)).filter(Boolean).join(', ');
  // Never String(object) — that is what put "[object Object]" on this page once already.
  if (typeof value === 'object') return '';
  return String(value);
};

const isBlank = (value) => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) {
    return value.filter((row) => row && Object.values(row).some((v) => v !== '' && v !== null && v !== undefined)).length === 0;
  }
  return false;
};

/**
 * Chrome-free on purpose: no navigation, no sign-in prompt, nothing that implies the visitor has
 * an account here. The only thing on the page is their form.
 */
/**
 * Every state of this page — loading, a dead link, the form itself — inside the same banner.
 *
 * The two error states are the ones that matter: somebody whose link has been switched off sees
 * an explanation on a firm-branded page rather than an amber box floating on grey, which is the
 * difference between "this was revoked" and "I have been sent somewhere broken".
 */
function Frame({ children }) {
  return (
    <div className="min-h-screen bg-page dark:bg-slate-950">
      <CandidateBanner title="Candidate conflict check and professional disclosure" width="max-w-3xl" />
      <div className="mx-auto max-w-3xl px-4 py-8">{children}</div>
    </div>
  );
}
